'use client';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import React, { useRef, useState } from 'react';
import { useClickOutside } from '@/hooks';
import { useUser } from '@/store/userStore';

import useData from './data';
import ProfilePicture from './ProfilePicture/ProfilePicture';
import ds from '@/styles/ds/ds.module.scss';

const Login = dynamic(() => import('@/components/auth/privy/Login/Login'), {
	ssr: false,
});

const ProfileDropdown = () => {
	const user = useUser();

	const [isOpen, setIsOpen] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);

	if (!user)
		return (
			<div className="flex gap-2.5">
				<Login className={`${ds.tertiary}  ${ds.hover_purpleOutlineWhite}`}>
					Login
				</Login>
				<Login
					className={`${ds.primary} ${ds.purpleWhite} ${ds.hover_purpleOutlineWhite}`}
				>
					Register
				</Login>
			</div>
		);

	return (
		<section className="flex gap-4 relative">
			<button
				ref={buttonRef}
				className="w-[39.5px] h-[39.5px]  rounded-full relative flex items-start z-10"
				onClick={(e) => {
					e.stopPropagation();
					setIsOpen(!isOpen);
				}}
				aria-label="Profile dropdown"
				aria-haspopup="true"
				aria-expanded={isOpen}
				aria-controls="profile-dropdown"
			>
				<ProfilePicture
					divProps={{ className: 'w-[39.5px] h-[39.5px]' }}
					isGlowing={isOpen}
				/>
			</button>
			<Modal isOpen={isOpen} setIsOpen={setIsOpen} buttonRef={buttonRef} />
		</section>
	);
};

interface ModalProps {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	buttonRef: React.RefObject<HTMLButtonElement>;
}

const Modal = ({ isOpen, setIsOpen, buttonRef }: ModalProps) => {
	const modalRef = useRef<HTMLDivElement>(null);
	useClickOutside(modalRef, () => setIsOpen(false), buttonRef);
	const { data } = useData();
	const user = useUser();

	return (
		<div
			ref={modalRef}
			className={`absolute top-[3.4rem] right-0 min-w-[200px] w-full  bg-teal-blue p-4   rounded-[5px]  ${
				isOpen ? 'block' : 'hidden'
			}

			`}
			style={{
				boxShadow: '0px 4px 10px 5px rgba(0,0, 0, 0.2)',
			}}
		>
			<p className="font-bold">{user?.username}</p>
			{data?.links?.map((item, index) => (
				<div
					key={index}
					className="flex justify-between items-center  font-bold motion-safe:hover:text-electric-sky duration-300 ease-in-out cursor-pointer px-4 py-2.5"
				>
					{item?.link ? (
						item.link.startsWith('http') ? (
							<a
								href={item.link}
								target="_blank"
								rel="noopener noreferrer"
								onClick={() => setIsOpen(false)}
								className="flex gap-2.5 items-center "
							>
								<span className="h-4 w-4 flex items-center justify-center">
									{item.icon}
								</span>
								{item.title}
							</a>
						) : (
							<Link
								href={item.link}
								onClick={() => setIsOpen(false)}
								className="flex gap-2.5 items-center"
							>
								<span className="h-4 w-4 flex items-center justify-center">
									{item.icon}
								</span>
								{item.title}
							</Link>
						)
					) : null}

					{item?.function ? (
						<button
							aria-label={item.title}
							className="flex gap-2.5 items-center"
							onClick={() => {
								item.function();
								setIsOpen(false);
							}}
						>
							<span className="h-4 w-4 flex items-center justify-center">
								{item?.icon}
							</span>
							{item.title}
						</button>
					) : null}
				</div>
			))}
			<hr className="   w-full border-powder-blue"></hr>
			<div className="flex justify-between items-center  font-bold motion-safe:hover:text-electric-sky duration-300 ease-in-out px-4 py-2.5">
				{data?.logout ? (
					<button
						aria-label="Logout"
						className="flex gap-2.5 items-center"
						onClick={() => {
							data?.logout?.function();
							setIsOpen(false);
						}}
					>
						<span className="h-4 w-4 flex items-center justify-center">
							{data?.logout?.icon}
						</span>
						{data?.logout?.title}
					</button>
				) : null}
			</div>
		</div>
	);
};

export default ProfileDropdown;
